import React, {Fragment, useState} from 'react';

import {browserHistory} from 'react-router';

import {GenericAttachedItemsSidebarItem} from 'app/components/general/GenericAttachedItemsSidebar/GenericAttachedItemsSidebarItem';
import {StyledListDIV} from 'app/components/general/VersionsSidebar/VersionsSidebar';
import {useInfiniteListFetcherRTK} from 'app/general/datafetchers/hooks/useInfiniteListFetcherRTK';
import {
  CloseURLSidebarPagePanels,
  StyledFooterDIV,
} from 'app/general/structures/DocSidebarPagePanels';
import {SweetLoadingBar} from 'app/general/sweet-loading-bar';
import {SearchInputField} from 'app/general/widgets/SearchInputField';
import StateButton from 'app/general/widgets/StateButton';
import {useModelChangeSubscription} from 'app/subscriptions';
import {SIDEBAR_PAGE_SIZE, SORT_BY_TYPES} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';

import type {
  CustomSidebarItemProps,
  GenericActionableType,
  Props as GenericAttachedItemSidebarProps,
} from 'app/components/general/GenericAttachedItemsSidebar/GenericAttachedItemsSidebarItem';
import type {UseListQueryType} from 'app/store/sweet-api/api-query';
import type {ModelChangeMessageType} from 'app/subscriptions';
import type {ComponentType, ReactElement, ReactNode} from 'react';

type Props<
  ItemType extends GenericActionableType,
  FetchParams extends {page: string},
  LinkedDocument extends {url: string},
  UseQueryHookType extends UseListQueryType<FetchParams, ItemType>
> = {
  useDataHook: UseQueryHookType;
  title: ReactNode;
  showFilterField: boolean;
  closeUrl: string;
  attachedItems: Array<LinkedDocument>;
  handleModelChange?: (message: ModelChangeMessageType) => unknown;
  itemNameProcessor: (item: ItemType) => string | ReactElement<'span'>;
  ItemComponent?: ComponentType<GenericAttachedItemSidebarProps<ItemType>>;
  itemComponentProps?:
    | CustomSidebarItemProps<ItemType>
    | ((item: ItemType, active: boolean) => CustomSidebarItemProps<ItemType>);
  canCreate: boolean;
  createURL: string;
  createLabel: string;
  addTooltipText?: string;
  disabledTooltipText?: string;
  searchPlaceholder: string;
  onRemove: (item: ItemType) => Promise<unknown>;
  onAttach: (item: ItemType) => Promise<unknown>;
  emptyResultsComponents: ReactNode;
  paramsConverter: (query: Record<string, unknown>) => FetchParams;
};

export function RTKGenericAttachedItemsSidebar<
  ItemType extends GenericActionableType,
  FetchParams extends {page: string},
  LinkedDocument extends {url: string},
  UseQueryHookType extends UseListQueryType<FetchParams, ItemType>
>({
  title,
  closeUrl,
  showFilterField,
  attachedItems,
  itemNameProcessor,
  itemComponentProps,
  onRemove,
  canCreate,
  createURL,
  createLabel,
  searchPlaceholder,
  onAttach,
  emptyResultsComponents,
  handleModelChange,
  useDataHook,
  paramsConverter,
  ItemComponent = GenericAttachedItemsSidebarItem,
}: Props<ItemType, FetchParams, LinkedDocument, UseQueryHookType>) {
  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  let {
    results: items,
    isFetching: loading,
    loadNext,
    hasNext,
  } = useInfiniteListFetcherRTK({
    fetchParams: {
      ordering: SORT_BY_TYPES.ALPHABETHICALLY,
      search: searchQuery,
      page: 1,
      page_size: SIDEBAR_PAGE_SIZE,
      search_fields: 'name',
    },
    useDataHook,
    paramsConverter,
  });

  useModelChangeSubscription((message) => {
    handleModelChange?.(message);
  }, []);

  const getItemProps = (item: ItemType, attached: boolean) => {
    if (itemComponentProps == null) {
      return {};
    }

    return typeof itemComponentProps === 'function'
      ? itemComponentProps(item, attached)
      : itemComponentProps;
  };

  const handleAttach = (newItem: ItemType) => {
    if (onAttach != null) {
      return onAttach(newItem);
    }

    throw 'Missing onAttach prop';
  };
  const handleRemove = (item: ItemType) => {
    if (onRemove != null) {
      return onRemove(item);
    }

    throw 'Missing onRemove prop';
  };

  const getAvailableItems = () => {
    const selectedItems = attachedItems.map((item) => item.url);

    return items.filter((item) => selectedItems.indexOf(item.url) === -1);
  };

  const getSelectedItems = () => {
    const selectedItems = attachedItems.map((item) => item.url);

    return items.filter((item) => selectedItems.indexOf(item.url) !== -1);
  };

  const search = (query: string) => {
    setSearchQuery(query);
  };

  const selectedItems = getSelectedItems();
  const availableItems = getAvailableItems();
  return (
    <CloseURLSidebarPagePanels
      title={title}
      closeUrl={closeUrl}
      additionalHeader={
        <Fragment>
          <div className="position-relative">
            <SweetLoadingBar loading={loading ? 1 : 0} />
          </div>
          {showFilterField ? (
            <SearchInputField
              className="form-control-sm"
              initialValue=""
              placeholder={searchPlaceholder}
              onChange={search}
              isSearching={loading}
            />
          ) : null}
        </Fragment>
      }
      footer={
        canCreate && (
          <StyledFooterDIV>
            <button
              className="btn btn-secondary btn-sm btn-block"
              onClick={() => {
                const query = searchQuery ? `?name=${searchQuery}` : '';
                browserHistory.push(`${createURL}${query}`);
              }}
            >
              <i className="fa fa-plus-circle" /> {createLabel}
            </button>
          </StyledFooterDIV>
        )
      }
    >
      {availableItems.length === 0 && selectedItems.length === 0 && !loading ? (
        emptyResultsComponents
      ) : (
        <StyledListDIV>
          <ul className="list-group">
            {selectedItems.map((item) => (
              <ItemComponent
                key={item.url}
                item={item}
                attached={true}
                onSelect={handleAttach}
                nameProcessor={itemNameProcessor}
                onRemove={handleRemove}
                {...getItemProps(item, true)}
              />
            ))}
            {availableItems.map((item) => (
              <ItemComponent
                onRemove={handleRemove}
                key={item.url}
                item={item}
                attached={false}
                nameProcessor={itemNameProcessor}
                onSelect={handleAttach}
                {...getItemProps(item, false)}
              />
            ))}
            {hasNext && (
              <div className="text-center my-2 mx-4">
                <StateButton
                  className="btn btn-outline-primary btn-sm btn-block"
                  busyText={gt('Loading...')}
                  isIdle={!loading}
                  onClick={loadNext}
                >
                  {gt('Load more')}
                </StateButton>
              </div>
            )}
          </ul>
        </StyledListDIV>
      )}
    </CloseURLSidebarPagePanels>
  );
}
